import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  campaignKindLabel,
  getPublicBrandCampaign,
  PublicBrandMarketItem,
} from '../../api/payment/brand-public-market.api';
import { contentFormatLabel } from '../../utils/brand-content-format';
import { ratingBadge } from '../../utils/brand-rating';
import { ordererProfilePath } from '../../utils/brand-profile';
import { routePaths } from '../../routes/routes-path';
import CampaignAction from './campaign-action';
import { formatDay, formatVnd } from './campaign-market.page';

/** Trang chi tiết một chiến dịch công khai: xem đủ thông tin không cần đăng nhập, nút nhận việc tuỳ người xem. */

type LoadState = 'loading' | 'ready' | 'gone' | 'error';

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex justify-between gap-3 border-b ui-divider py-1.5 text-[13px] last:border-b-0">
    <span className="ui-muted">{label}</span>
    <span className="ui-text text-right font-medium">{value}</span>
  </div>
);

const CampaignDetailPage: React.FC = () => {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const [item, setItem] = useState<PublicBrandMarketItem | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading');
    getPublicBrandCampaign(campaignId)
      .then((res) => {
        if (!alive) return;
        setItem(res);
        setState('ready');
      })
      .catch((err) => {
        if (!alive) return;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        // 400 = id không đúng dạng; coi như chiến dịch không còn, giống 404.
        setState(status === 404 || status === 400 ? 'gone' : 'error');
      });
    return () => {
      alive = false;
    };
  }, [campaignId]);

  if (state === 'loading') {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }

  if (state !== 'ready' || !item) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center gap-3 px-4 py-10 text-center">
        <p className="ui-text text-[15px]">
          {state === 'gone'
            ? 'Chiến dịch này không còn nhận việc.'
            : 'Không tải được chiến dịch, thử lại sau.'}
        </p>
        <Link
          to={routePaths.campaigns}
          className="font-semibold text-[#0057FF] underline-offset-2 hover:underline dark:text-blue-300"
        >
          Xem các chiến dịch khác
        </Link>
      </div>
    );
  }

  const formatName = item.contentFormat
    ? contentFormatLabel(item.contentFormat)
    : null;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-4">
      <Link
        to={routePaths.campaigns}
        className="ui-muted w-fit text-[13px] underline-offset-2 hover:underline"
      >
        Quay lại sàn chiến dịch
      </Link>

      <div className="ui-card flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="ui-heading text-[15px] font-bold">
            {item.brandName}
          </span>
          <span className="ui-muted text-[12px]">
            {ratingBadge(
              item.businessRatingAverage,
              item.businessRatingCount ?? 0
            )}
          </span>
          <a
            href={ordererProfilePath(item.adAccountId)}
            className="text-[12px] text-[#0057FF] underline-offset-2 hover:underline dark:text-blue-300"
          >
            Xem hồ sơ doanh nghiệp
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[#EAEFFA] px-2 py-0.5 text-[11px] font-semibold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
            {campaignKindLabel(item.kind)}
          </span>
          {formatName && (
            <span className="ui-muted text-[12px]">{formatName}</span>
          )}
        </div>
        <h1 className="ui-heading text-[20px] font-bold">{item.title}</h1>

        {item.images.length > 0 && (
          <div className="flex gap-2 overflow-x-auto">
            {item.images.map((img) => (
              <img
                key={img.id}
                src={img.url}
                alt=""
                className="h-48 shrink-0 rounded-lg object-cover"
              />
            ))}
          </div>
        )}

        {item.summary && (
          <p className="ui-text whitespace-pre-line text-[14px]">
            {item.summary}
          </p>
        )}

        <div className="flex flex-col">
          <Row
            label="Creator nhận"
            value={
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                {formatVnd(item.netVnd)}
              </span>
            }
          />
          <Row
            label="Số suất"
            value={`Còn ${item.slotsLeft}/${item.slotsTotal} suất`}
          />
          {item.applyDeadline && (
            <Row label="Hạn nhận" value={formatDay(item.applyDeadline)} />
          )}
          {item.minFollowers > 0 && (
            <Row
              label="Điều kiện"
              value={`Từ ${item.minFollowers.toLocaleString(
                'vi-VN'
              )} người theo dõi`}
            />
          )}
          {item.kind !== 'Direct' &&
            item.submitDays != null &&
            item.submitDays > 0 && (
              <Row
                label="Thời gian nộp bài"
                value={`${item.submitDays} ngày`}
              />
            )}
          {item.kind !== 'Direct' && (
            <Row label="Số lần sửa tối đa" value={item.maxRevisions} />
          )}
          {item.holdDays > 0 && (
            <Row label="Giữ bài trên trang" value={`${item.holdDays} ngày`} />
          )}
        </div>

        <CampaignAction item={item} />
      </div>
    </div>
  );
};

export default CampaignDetailPage;
